import { isReference } from '@apollo/client';
import type {
  FetchResult,
  InternalRefetchQueriesInclude,
  StoreObject,
} from '@apollo/client';
import dayjs from 'dayjs';

import { isDevelopment } from '@constants/env';

import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import { AppointmentRecord } from '@models/appointment';
import type { GetFhirRecordParams } from '@models/fhir-resource';

import { parseArgs, type CachedRecordList } from '@utils/apollo';

import {
  useListAppointmentsQuery,
  useGetAppointmentQuery,
  usePostAppointmentRecordMutation,
  useUpdateAppointmentRecordMutation,
  useDeleteAppointmentRecordMutation,
} from 'generated/appointment.generated';
import type {
  PostAppointmentRecordMutationVariables,
  UpdateAppointmentRecordMutationVariables,
  DeleteAppointmentRecordMutation,
  DeleteAppointmentRecordMutationVariables,
} from 'generated/appointment.generated';
import { GetAllCuratorTaskRecordsForPatientDocument } from 'generated/curator-task.generated';
import type { AppointmentFilterInput } from 'generated/types';

export const useGetAppointments = (
  filters?: AppointmentFilterInput,
  skip?: boolean
) => {
  const skipQuery = skip ?? false;
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';

  const response = useListAppointmentsQuery({
    variables: {
      input: {
        patient: patient_id,
        count: 1500,
        page: 1,
        ...(filters && {
          filters,
        }),
      },
    },
    skip: !patient_id || skipQuery,
  });
  const { data, error } = response;
  handleError(error);

  const records = (data?.listAppointments.data ?? []).map(
    (r) => new AppointmentRecord(r)
  );
  return { ...response, data: records };
};

export const useGetAppointment = (params: GetFhirRecordParams) => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const { record_id } = params;
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useGetAppointmentQuery({
    variables: {
      input: {
        record_id: params.record_id,
        patient_id,
      },
    },
    skip:
      !patient_id || !record_id || record_id === 'add' || Boolean(params.skip),
  });
  const { data, error } = response;
  handleError(error);
  const record = data?.getAppointment;
  return { ...response, data: record ? new AppointmentRecord(record) : null };
};

export function useCreateAppointment(): [
  (
    params: PostAppointmentRecordMutationVariables
  ) => Promise<FetchResult<AppointmentRecord>>,
  boolean,
] {
  const {
    state: { activeProfile, isCurator },
  } = useSession();
  const [mutate, { loading: baseLoading }] = usePostAppointmentRecordMutation();
  const loading = baseLoading;
  const { handleError } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'error',
  });

  async function createAppointment(
    params: PostAppointmentRecordMutationVariables
  ) {
    const { appointment } = params.input;
    const refetchQueries: InternalRefetchQueriesInclude = [];
    // Refetch all curator tasks for a patient if an upcoming appt was created
    if (isCurator && dayjs(appointment.start).diff(dayjs(), 'day')) {
      refetchQueries.push({
        query: GetAllCuratorTaskRecordsForPatientDocument,
        variables: {
          input: {
            vitall_patient_id: activeProfile?.vitall_patient_id,
          },
        },
      });
    }

    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.postAppointmentRecord) {
          return;
        }
        const storeObject =
          response.postAppointmentRecord as unknown as StoreObject;
        try {
          cache.modify<{ listAppointments: CachedRecordList }>({
            fields: {
              listAppointments: (existing, { storeFieldName, toReference }) => {
                if (isReference(existing)) {
                  return existing;
                }
                const parsed = parseArgs(storeFieldName);
                const paramsPatientId =
                  appointment.participant.at(0)?.actor?.reference;
                if (parsed?.patient === paramsPatientId) {
                  return {
                    ...existing,
                    data: [...existing.data, toReference(storeObject)],
                  };
                }
                return existing;
              },
            },
          });
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
      refetchQueries,
    });
    if (response.errors) {
      handleError(response.errors);
    }

    const createdAppt = response.data?.postAppointmentRecord;
    return {
      ...response,
      data: createdAppt ? new AppointmentRecord(createdAppt) : undefined,
    };
  }
  return [createAppointment, loading];
}

export function useUpdateAppointment(): [
  (
    params: UpdateAppointmentRecordMutationVariables
  ) => Promise<FetchResult<AppointmentRecord>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useUpdateAppointmentRecordMutation();
  const { handleError } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'error',
  });
  const loading = baseLoading;

  async function updateAppointment(
    params: UpdateAppointmentRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
    });
    if (response.errors) {
      handleError(response.errors);
    }
    const updatedAppt = response.data?.updateAppointmentRecord;
    return {
      ...response,
      data: updatedAppt ? new AppointmentRecord(updatedAppt) : undefined,
    };
  }
  return [updateAppointment, loading];
}

export function useDeleteAppointment(): [
  (
    params: DeleteAppointmentRecordMutationVariables
  ) => Promise<FetchResult<DeleteAppointmentRecordMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useDeleteAppointmentRecordMutation();
  const loading = baseLoading;

  async function deleteAppointment(
    params: DeleteAppointmentRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.deleteAppointmentRecord) {
          return;
        }
        const { appointment_id: id } = params.input;
        try {
          const normalizedId = cache.identify({
            id,
            __typename: 'AppointmentModel',
          });
          cache.evict({ id: normalizedId });
          cache.gc();
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    return response;
  }
  return [deleteAppointment, loading];
}
